// src/contexts/AuthContext.js - Enhanced with charger context support (preserving ALL existing functionality)
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { login as loginAPI, logout as logoutAPI, getCurrentUser, buildChargerDetailsUrl } from '../api/authAPI';
import { getDriverRFIDCards } from '../api/rfidAPI';

// Auth Context
const AuthContext = createContext();

// Auth Actions
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_USER: 'SET_USER',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_CHARGER_CONTEXT: 'SET_CHARGER_CONTEXT', // NEW
  CLEAR_CHARGER_CONTEXT: 'CLEAR_CHARGER_CONTEXT', // NEW
  SET_QR_LOGIN: 'SET_QR_LOGIN', // NEW: Track QR login state
  SET_DRIVER_RFID: 'SET_DRIVER_RFID', // NEW: Store driver RFID details
  CLEAR_DRIVER_RFID: 'CLEAR_DRIVER_RFID' // NEW: Clear driver RFID details
};

// Initial state
const initialState = {
  user: null,
  token: localStorage.getItem('access_token'),
  isAuthenticated: false,
  isLoading: true,
  error: null,
  activeChargerContext: JSON.parse(localStorage.getItem('active_charger_context') || 'null'), // NEW: Store active charger context
  isQrLogin: localStorage.getItem('is_qr_login') === 'true', // NEW: Track if user logged in via QR code
  driverRFID: JSON.parse(localStorage.getItem('driver_rfid') || 'null') // NEW: Store driver RFID card details
};

// Auth reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null
      };
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.access_token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
    
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      };
    
    case AUTH_ACTIONS.LOGOUT:
      // Clear localStorage items
      localStorage.removeItem('active_charger_context');
      localStorage.removeItem('is_qr_login');
      localStorage.removeItem('driver_rfid');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        activeChargerContext: null, // NEW: Clear charger context on logout
        isQrLogin: false, // NEW: Clear QR login state on logout
        driverRFID: null // NEW: Clear driver RFID on logout
      };
    
    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false
      };
    
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
    
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    
    // NEW: Charger context actions
    case AUTH_ACTIONS.SET_CHARGER_CONTEXT:
      // Persist to localStorage
      localStorage.setItem('active_charger_context', JSON.stringify(action.payload));
      return {
        ...state,
        activeChargerContext: action.payload
      };
    
    case AUTH_ACTIONS.CLEAR_CHARGER_CONTEXT:
      // Remove from localStorage
      localStorage.removeItem('active_charger_context');
      return {
        ...state,
        activeChargerContext: null
      };
    
    case AUTH_ACTIONS.SET_QR_LOGIN:
      // Persist to localStorage
      localStorage.setItem('is_qr_login', action.payload.toString());
      return {
        ...state,
        isQrLogin: action.payload
      };
    
    case AUTH_ACTIONS.SET_DRIVER_RFID:
      // Persist to localStorage
      localStorage.setItem('driver_rfid', JSON.stringify(action.payload));
      return {
        ...state,
        driverRFID: action.payload
      };
    
    case AUTH_ACTIONS.CLEAR_DRIVER_RFID:
      // Remove from localStorage
      localStorage.removeItem('driver_rfid');
      return {
        ...state,
        driverRFID: null
      };
    
    default:
      return state;
  }
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check if user is authenticated on app load
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('access_token');
      
      if (token) {
        try {
          // Verify token is still valid by fetching user info
          const userData = await getCurrentUser();
          dispatch({
            type: AUTH_ACTIONS.SET_USER,
            payload: userData
          });
        } catch (error) {
          // Token is invalid, remove it
          localStorage.removeItem('access_token');
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    initializeAuth();
  }, []);

  // ENHANCED: Login function now supports charger parameters for QR code login
  const login = async (email, password, chargerName = null, connectorNumber = null) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    
    try {
      // Call enhanced login API with optional charger parameters
      const response = await loginAPI(email, password, chargerName, connectorNumber);
      
      // Store token in localStorage
      localStorage.setItem('access_token', response.access_token);
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: response
      });
      
      // NEW: Set charger context if provided
      if (response.charger_context) {
        dispatch({
          type: AUTH_ACTIONS.SET_CHARGER_CONTEXT,
          payload: response.charger_context
        });
      }
      
      // NEW: Set QR login state if charger parameters were provided
      if (chargerName && connectorNumber) {
        dispatch({
          type: AUTH_ACTIONS.SET_QR_LOGIN,
          payload: true
        });
      }
      
      // NEW: Fetch driver RFID cards if user is a driver and QR login
      if (response.user && response.user.role === 'Driver' && response.user.driver_id && chargerName && connectorNumber) {
        try {
          const rfidCards = await getDriverRFIDCards(response.user.driver_id, response.user.company_id);
          
          // Find the first enabled RFID card
          const activeRFID = rfidCards.find(card => card.RFIDCardEnabled);
          
          if (activeRFID) {
            dispatch({
              type: AUTH_ACTIONS.SET_DRIVER_RFID,
              payload: activeRFID
            });
            console.log('🔑 Driver RFID card loaded:', activeRFID.RFIDCardId);
          } else {
            console.warn('⚠️ No active RFID card found for driver');
          }
        } catch (rfidError) {
          console.error('❌ Failed to fetch driver RFID cards:', rfidError);
          // Don't fail the login if RFID fetch fails
        }
      }
      
      // NEW: Return enhanced response with charger context for navigation
      return {
        ...response,
        success: true,
        redirectUrl: response.charger_context ? 
          buildChargerDetailsUrl(response.charger_context) : null
      };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Login failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage
      });
      throw error;
    }
  };

  // Logout function (existing functionality preserved)
  const logout = async () => {
    try {
      // Call backend logout endpoint
      await logoutAPI();
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with logout even if API call fails
    } finally {
      // Remove token from localStorage
      localStorage.removeItem('access_token');
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  // Clear error function (existing)
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // NEW: Charger context management functions
  const setActiveChargerContext = (chargerContext) => {
    dispatch({
      type: AUTH_ACTIONS.SET_CHARGER_CONTEXT,
      payload: chargerContext
    });
  };

  const clearActiveChargerContext = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_CHARGER_CONTEXT });
  };

  // NEW: Driver RFID management functions
  const setDriverRFID = (rfidCard) => {
    dispatch({
      type: AUTH_ACTIONS.SET_DRIVER_RFID,
      payload: rfidCard
    });
  };

  const clearDriverRFID = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_DRIVER_RFID });
  };

  // NEW: Function to get driver's RFID ID for transactions
  const getDriverRFIDId = () => {
    return state.driverRFID?.RFIDCardId || null;
  };

  // Role-based access control helpers (existing functionality preserved)
  const hasRole = (requiredRole) => {
    if (!state.user) return false;
    
    const roleHierarchy = {
      'SuperAdmin': 3,
      'Admin': 2,
      'Driver': 1
    };
    
    return roleHierarchy[state.user.role] >= roleHierarchy[requiredRole];
  };

  const canAccessCompany = (companyId) => {
    if (!state.user) return false;
    
    // SuperAdmin can access all companies
    if (state.user.role === 'SuperAdmin') {
      return true;
    }
    
    // Admin and Driver can only access their own company
    return state.user.company_id === companyId;
  };

  // Role check functions (existing functionality preserved)
  const isSuperAdmin = () => state.user?.role === 'SuperAdmin';
  const isAdmin = () => state.user?.role === 'Admin';
  const isDriver = () => state.user?.role === 'Driver';

  const value = {
    // State (existing)
    user: state.user,
    token: state.token,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    
    // NEW: Charger context state
    activeChargerContext: state.activeChargerContext,
    isQrLogin: state.isQrLogin,
    
    // NEW: Driver RFID state
    driverRFID: state.driverRFID,
    
    // Actions (existing)
    login, // Enhanced with charger support
    logout,
    clearError,
    
    // NEW: Charger context actions
    setActiveChargerContext,
    clearActiveChargerContext,
    
    // NEW: Driver RFID actions
    setDriverRFID,
    clearDriverRFID,
    getDriverRFIDId,
    
    // Role helpers (existing)
    hasRole,
    canAccessCompany,
    isSuperAdmin,
    isAdmin,
    isDriver
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context (existing)
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};